import { ProfileComponent, ProfileComponentType } from "@authory-packages/core";
import { getSocialProfileUrl } from "./getSocialProfileUrl";

const IDENTITY_LINK_TYPES = new Set<ProfileComponentType>([
    ProfileComponentType.InstagramLink,
    ProfileComponentType.FacebookLink,
    ProfileComponentType.LinkedinLink,
    ProfileComponentType.TwitterLink,
    ProfileComponentType.BlueskyLink,
    ProfileComponentType.PinterestLink,
    ProfileComponentType.ThreadsLink,
    ProfileComponentType.SoundcloudLink,
    ProfileComponentType.TikTokLink,
    ProfileComponentType.YoutubeLink,
    ProfileComponentType.PatreonLink,
    ProfileComponentType.RedditLink,
    ProfileComponentType.QuoraLink,
    ProfileComponentType.TumblrLink,
    ProfileComponentType.MastodonLink,
    ProfileComponentType.VimeoLink,
    ProfileComponentType.PersonalLink
]);

/**
 * Whether a profile link points at another profile or site of the same person, which earns rel="me" and a sameAs entry.
 * @param type The profile component type
 * @returns True for social profiles and the personal website
 */
export function isIdentityLink(type: ProfileComponentType) {
    return IDENTITY_LINK_TYPES.has(type);
}

/**
 * Collects the absolute urls of the identity links the owner added to their profile.
 * @param components The profile components
 * @returns The unique web urls, in profile order
 */
export function getIdentityLinkUrls(components: ProfileComponent[] = []) {
    const urls = components
        .filter((component) => isIdentityLink(component.type))
        .map((component) => getSocialProfileUrl(component.type, component.url || component.text || ""))
        .filter(isWebUrl);

    return [...new Set(urls)];
}

/**
 * Whether a link is a real web address. Values are user entered, so "mailto:me@x.com" saved as a website comes out as
 * "https://mailto:me@x.com", which parses as credentials on x.com.
 * @param url The link
 * @returns True for http(s) urls with a dotted host and no credentials
 */
function isWebUrl(url: string) {
    try {
        const { protocol, hostname, username, password } = new URL(url);

        return (protocol === "https:" || protocol === "http:") && hostname.includes(".") && !username && !password;
    } catch {
        return false;
    }
}
